import React, { FC, useContext, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player/lazy";
import { useSwiperSlide } from "swiper/react";
import NavContext from "../Layout/Nav/NavProvider";
import { PlayerControls } from "../PlayerControls";

type VideoStateType = {
  played: number;
  playedSeconds: number;
  loaded: number;
  loadedSeconds: number;
};

type PlayerProps = {
  videoId: string;
  mobileVideoId: string;
  autoPlay: boolean;
  alwaysMute: boolean;
  isMobileAutoplayActive: boolean;
  isFullScreen: boolean;
  showMobileVideo: boolean;
};
const Player: FC<PlayerProps> = (props) => {
  const {
    videoId,
    mobileVideoId,
    autoPlay,
    alwaysMute,
    isMobileAutoplayActive,
    isFullScreen,
    showMobileVideo,
  } = props;
  const { userInteracted, isMobile } = useContext(NavContext);
  const [isMobileActive, setIsMobileActive] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [videoProgressPercentage, setVideoProgressPercentage] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [volume, setVolume] = useState<number>(0);
  const [playing, setPlaying] = useState<boolean>(true);
  const swiperSlide = useSwiperSlide();
  const playerRef = useRef<any>();
  const youtubeConfig = {
    youtube: {
      playerVars: {
        rel: 0,
        autoplay: 1,
      },
      embedOptions: {
        suggestedQuality: "hd1080",
      },
    },
  };
  function managePlayState(): boolean {
    if (!autoPlay) {
      return false;
    }

    return swiperSlide || isMobileAutoplayActive != null
      ? playing
      : isMobileAutoplayActive;
  }

  function onProgressChanged(seconds: number) {
    const currentProgress = (seconds / videoDuration) * 100;
    setVideoProgressPercentage(currentProgress);
  }

  function onDurationLoaded(duration: number) {
    setVideoDuration(duration);
  }

  function getVideoUrl() {
    const videoIdValue =
      isMobileActive && showMobileVideo && mobileVideoId
        ? mobileVideoId
        : videoId;
    
    return `https://www.youtube.com/embed/${videoIdValue}`;
  }

  function onVolumechange(newVolume: number) {
    setVolume(newVolume);
    if (isMuted) {
      setIsMuted(false);
    }
  }

  const onCurrentProgressChange = (newProgress: number) => {
    if (!playerRef?.current) {
      return;
    }
    onProgressChanged(newProgress);
    playerRef.current.seekTo(newProgress, "seconds");
  };

  useEffect(() => {
    const value = isMobile();
    setIsMobileActive(value);
  }, [isMobile]);

  useEffect(() => {
    if (swiperSlide?.isActive) {
      setPlaying(swiperSlide.isActive);
    }
  }, [swiperSlide?.isActive]);

  useEffect(() => {
    if (!alwaysMute && userInteracted && isFullScreen) {
      setVolume(1);
      setIsMuted(false);
      return;
    }
    setIsMuted(true);
  }, [userInteracted, isFullScreen]);

  return (
    <>
      <ReactPlayer
        ref={playerRef}
        url={getVideoUrl()}
        width="100%"
        height="100%"
        controls={false}
        onDuration={onDurationLoaded}
        playing={managePlayState()}
        onProgress={(state: VideoStateType) =>
          isFullScreen ? onProgressChanged(state.playedSeconds) : null
        }
        loop={true}
        muted={isMuted}
        volume={volume}
        style={{ position: "absolute", overflow: "hidden", top: 0, left: 0 }}
        config={youtubeConfig}
      />
      {isFullScreen && (
        <PlayerControls
          isMobile={isMobileActive}
          isPlaying={playing}
          onChangeVolume={(newValue) => onVolumechange(newValue)}
          onPause={setPlaying}
          videoProgress={videoProgressPercentage}
          volume={volume}
          onSetCurrentProgress={onCurrentProgressChange}
          videoDuration={videoDuration}
        />
      )}
    </>
  );
};

export default Player;
